import type { ExpRateChartSnapshot } from '@/domain/types'
import type { AppLocale } from '@/i18n/locales'
import { expChartCopy } from '@/i18n/exp-chart'
import { expMeaningCopy } from '@/i18n/exp-meaning'
import { formatRate, type RateUnit } from '@/core/display-settings'
import { presentExpRateChart } from './exp-rate-chart'

/** One presentation for chart overlays and independent live readouts. No sampling here. */
export function presentExpChartReadout(model: ExpRateChartSnapshot | undefined, locale: AppLocale,
  rateUnit: RateUnit = 'hour', widget = false, chart = presentExpRateChart(model)) {
  const copy = expChartCopy[locale]
  const format = (value: number) => formatRate(value, locale, rateUnit, { maximumFractionDigits: widget || rateUnit === 'hour' ? 1 : 2 })
  const unit = `${model?.unit === 'percentage-points' ? ' pp' : ''}${rateUnit === 'minute' ? '/min' : '/h'}`
  const status = copy[model?.recent.status === 'ready' && !chart.dots.length ? 'warming' : model?.recent.status ?? 'unavailable']
  const sample = model?.recent
  const accumulation = sample?.status === 'warming' && sample.continuousMs !== undefined
    ? expMeaningCopy[locale].accumulating.replace('{seconds}', String(Math.floor(sample.continuousMs / 1000))).replace('{window}', String((sample.windowMs ?? 60_000) / 1000)) : null
  const ready = sample?.status === 'ready' && sample.value != null && Number.isFinite(sample.value)
  const recent = ready ? format(sample.value!) : widget ? '--' : '—'
  let tone: 'neutral' | 'above' | 'below' = 'neutral'
  const mean = model?.average
  if (ready && mean != null && Number.isFinite(mean)) {
    const difference = sample.value! - mean
    const tolerance = Math.abs(mean) * .02 + Number.EPSILON * Math.max(1, Math.abs(sample.value!), Math.abs(mean)) * 4
    tone = Math.abs(difference) <= tolerance ? 'neutral' : difference < 0 ? 'below' : 'above'
  }
  const average = chart.average ? `${copy.average} ${format(chart.average.value)}${unit}${chart.average.edge ? ` · ${copy[chart.average.edge]}` : ''}` : ''
  const description = `${copy.title} · ${copy.ready} · ${copy.recent} ${recent}${unit} · ${accumulation ?? status}${average ? ` · ${average}` : ''}`
  return { recent, tone, unit, status, accumulation, average, description, ready }
}
